import type {
  ConnectorReportRow,
  ConnectorTrafficCounters,
  WorkflowReportRow,
} from "../../../shared/connectorUsageReport";

const DAY_MS = 86_400_000;

// Usage the ledger could not attribute to a space: connection tests, audit log
// delivery, and other workflow-less surfaces.
export const NO_SPACE = "__unattributed__";

export type {
  ConnectorReportRow,
  WorkflowReportRow,
} from "../../../shared/connectorUsageReport";

export type TrafficTotals = {
  requests: number;
  errors: number;
};

export type ConnectorSummary = TrafficTotals & {
  connectorId: string;
};

export type WorkflowSummary = TrafficTotals & {
  workflowId: string;
  connectorId: string;
  spaceId: string;
};

export type DailyVolume = {
  day: number;
  total: number;
  errors: number;
  byConnector: ReadonlyMap<string, number>;
};

function emptyTotals(): TrafficTotals {
  return { requests: 0, errors: 0 };
}

function matchesSpace(rowSpaceId: string | null, spaceId: string): boolean {
  return spaceId === NO_SPACE ? rowSpaceId == null : rowSpaceId === spaceId;
}

export function filterUsageRows<
  T extends { spaceId: string | null; connectorId: string },
>(
  rows: readonly T[],
  spaceId: string | null,
  connectorId: string | null
): readonly T[] {
  if (spaceId == null && connectorId == null) return rows;
  return rows.filter(
    (row) =>
      (connectorId == null || row.connectorId === connectorId) &&
      (spaceId == null || matchesSpace(row.spaceId, spaceId))
  );
}

function groupTotals<T extends ConnectorTrafficCounters, K>(
  rows: readonly T[],
  keyOf: (row: T) => K
): Map<K, TrafficTotals> {
  const map = new Map<K, TrafficTotals>();
  for (const row of rows) {
    const key = keyOf(row);
    let totals = map.get(key);
    if (!totals) {
      totals = emptyTotals();
      map.set(key, totals);
    }
    totals.requests += row.requestCount;
    totals.errors += row.errorCount;
  }
  return map;
}

function byRequestsThen<T extends TrafficTotals>(
  tiebreak: (row: T) => string
): (a: T, b: T) => number {
  return (a, b) =>
    b.requests - a.requests || tiebreak(a).localeCompare(tiebreak(b));
}

export function computeConnectorSummaries(
  rows: readonly ConnectorReportRow[]
): ConnectorSummary[] {
  return [...groupTotals(rows, (row) => row.connectorId)]
    .map(([connectorId, totals]) => ({ connectorId, ...totals }))
    .sort(byRequestsThen((row) => row.connectorId));
}

export function computeWorkflowSummaries(
  rows: readonly WorkflowReportRow[]
): WorkflowSummary[] {
  const map = new Map<string, WorkflowSummary>();
  for (const row of rows) {
    const key = `${row.workflowId}/${row.connectorId}`;
    let summary = map.get(key);
    if (!summary) {
      summary = {
        workflowId: row.workflowId,
        connectorId: row.connectorId,
        spaceId: row.spaceId,
        ...emptyTotals(),
      };
      map.set(key, summary);
    }
    summary.requests += row.requestCount;
    summary.errors += row.errorCount;
  }
  return [...map.values()].sort(
    byRequestsThen((row) => `${row.workflowId}/${row.connectorId}`)
  );
}

export function computeTrafficOverview(
  summaries: readonly ConnectorSummary[]
): TrafficTotals {
  const totals = emptyTotals();
  for (const summary of summaries) {
    totals.requests += summary.requests;
    totals.errors += summary.errors;
  }
  return totals;
}

// Every day in the window gets a column, so a gap in the ledger reads as a
// zero-height bar rather than compressing the axis.
export function computeDailyVolume(
  rows: readonly ConnectorReportRow[],
  since: number,
  now: number
): DailyVolume[] {
  const days = new Map<
    number,
    { total: number; errors: number; byConnector: Map<string, number> }
  >();
  const lastDay = Math.floor(now / DAY_MS) * DAY_MS;
  for (
    let day = Math.floor(since / DAY_MS) * DAY_MS;
    day <= lastDay;
    day += DAY_MS
  ) {
    days.set(day, { total: 0, errors: 0, byConnector: new Map() });
  }
  for (const row of rows) {
    const bucket = days.get(row.day);
    if (!bucket) continue;
    const requests = row.requestCount;
    bucket.errors += row.errorCount;
    bucket.total += requests;
    bucket.byConnector.set(
      row.connectorId,
      (bucket.byConnector.get(row.connectorId) ?? 0) + requests
    );
  }
  return [...days]
    .map(([day, bucket]) => ({ day, ...bucket }))
    .sort((a, b) => a.day - b.day);
}

export function connectorUsageSpaceIds(
  rows: readonly { spaceId: string | null }[]
): {
  spaceIds: string[];
  hasUnattributed: boolean;
} {
  const spaceIds = new Set<string>();
  let hasUnattributed = false;
  for (const row of rows) {
    if (row.spaceId == null) hasUnattributed = true;
    else spaceIds.add(row.spaceId);
  }
  return { spaceIds: [...spaceIds], hasUnattributed };
}

export function formatErrorRatePercent(pct: number): string {
  if (pct <= 0) return "0%";
  if (pct < 0.1) return "<0.1%";
  return `${pct >= 10 ? Math.round(pct) : Number(pct.toFixed(1))}%`;
}

export function formatErrorRate(errors: number, requests: number): string {
  if (requests === 0) return "—";
  return formatErrorRatePercent((errors / requests) * 100);
}

export function formatRequestCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 10_000) return `${Math.round(count / 1000)}K`;
  return count.toLocaleString("en-US");
}
